import BusinessList from '@/components/business/BusinessList';
import DownloadApp from '@/components/shared/DownloadApp';
import { ENV } from '@/core/config/env';
import { BusinessItem } from '@/core/models/businesses/businessList';
import Head from 'next/head';
import Link from 'next/link';

export interface ServiceCategoryPageProps {
    businesses: BusinessItem[];
    pagination: { count: number; currentPage: number; totalPages: number };
    city: string;
    cityName: string;
    categoryId: string;
    categoryLabel: string;
    categorySlug: string;
    categoryDescription: string;
    tags: string[];
}

export default function ServiceCategoryPage({
    businesses,
    pagination,
    city,
    cityName,
    categoryId,
    categoryLabel,
    categorySlug,
    categoryDescription,
    tags,
}: ServiceCategoryPageProps) {
    const searchParams = {
        category: categoryId,
        text: '',
        city,
        province: '',
        suburb: '',
        page: 1,
    };

    const pageTitle = `${categoryLabel} in ${cityName} | Car Spares EzyFind`;
    const pageDescription = `Find trusted ${categoryLabel.toLowerCase()} services in ${cityName}. ${categoryDescription} Browse and compare providers near you.`;
    const canonicalUrl = `${ENV.DOMAIN_URL}${categorySlug}/${city}/`;

    const keywords = [
        `${categoryLabel} ${cityName}`,
        `${categoryLabel.toLowerCase()} near me`,
        `${categoryLabel.toLowerCase()} South Africa`,
        `automotive ${categoryLabel.toLowerCase()} ${cityName}`,
    ].join(', ');

    return (
        <>
            <Head>
                <title>{pageTitle}</title>
                <meta name="description" content={pageDescription} />
                <meta name="keywords" content={keywords} />
                <link rel="canonical" href={canonicalUrl} />
                <meta property="og:title" content={pageTitle} />
                <meta property="og:description" content={pageDescription} />
                <meta property="og:url" content={canonicalUrl} />
                <meta property="og:type" content="website" />
            </Head>

            <section className="relative overflow-hidden bg-[#0f1512] text-white">
                <div
                    className="absolute inset-0 opacity-35"
                    style={{
                        backgroundImage:
                            'radial-gradient(circle at 15% 20%, rgba(220, 38, 38, 0.35), transparent 45%), radial-gradient(circle at 80% 10%, rgba(239, 68, 68, 0.3), transparent 40%)',
                    }}
                />
                <div className="container relative py-12 md:py-16">
                    <div className="grid gap-10 lg:grid-cols-12 lg:items-center">
                        <div className="lg:col-span-7">
                            <nav className="text-sm text-red-200 mb-3">
                                <Link href={`/${categorySlug}/`} className="hover:text-white">
                                    {categoryLabel}
                                </Link>
                                <span className="mx-1">&gt;</span>
                                <span className="text-white">{cityName}</span>
                            </nav>
                            <p className="text-xs uppercase tracking-[0.3em] text-red-200">
                                Automotive Services Directory
                            </p>
                            <h1 className="mt-3 text-3xl md:text-5xl font-bold leading-tight">
                                {categoryLabel} in {cityName}
                            </h1>
                            <p className="mt-4 text-base md:text-lg text-red-100">
                                {categoryDescription} Find trusted {categoryLabel.toLowerCase()} providers in{' '}
                                {cityName}, South Africa.
                            </p>
                            <div className="mt-6 flex flex-wrap items-center gap-3">
                                <Link
                                    href="#business-filters"
                                    className="bg-white text-slate-900 px-5 py-2.5 rounded-md text-sm font-semibold shadow"
                                >
                                    Find Providers
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <div className="my-10" id="business-filters">
                <div className="container">
                    <BusinessList
                        searchParams={searchParams}
                        initialBusinesses={businesses}
                        initialPagination={pagination}
                    />
                </div>
            </div>

            <section className="container my-12">
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                    <p className="text-xs uppercase tracking-[0.25em] text-red-700">Services Directory</p>
                    <h2 className="mt-3 text-2xl font-semibold text-slate-900">
                        {categoryLabel} Services in {cityName}
                    </h2>
                    <p className="mt-3 text-sm text-slate-600">{categoryDescription}</p>
                    {tags.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2">
                            {tags.map((tag) => (
                                <span
                                    key={tag}
                                    className="rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700"
                                >
                                    {tag}
                                </span>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            <DownloadApp />
        </>
    );
}
